import React from 'react';
import { apiRequest } from '../actions/apiActions';
import { showLoading, hideLoading, addUiMsg } from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import {
  LOAD_ALERT_DEVICE_DETAILS,
  PROCESS_ALERT_DEVICE_DETAILS,
  addAlertDeviceDetails,
  LOAD_ALERT_DEVICE_TYPES,
  PROCESS_ALERT_DEVICE_TYPES,
  PROCESS_UNASSIGNED_ALERT_DEVICE_DETAILS,
  LOAD_UNASSIGNED_ALERT_DEVICE_DETAILS,
  addAlertDeviceTypes,
  GET_ALL_ALERT_DEVICES,
  LOAD_ASSIGNED_ALERT_GROUP_LIST,
  PROCESS_ASSIGNED_ALERT_GROUP_LIST,
  addAlertGroupList,
  LOAD_ALERT_DEVICE_CATEGORIES,
  PROCESS_ALERT_DEVICE_CATEGORIES,
  addAlertDeviceCategories,
  CREATE_ASSOCIATE_ALERT_DEVICE,
  PROCESS_ASSOCIATE_NEW_ALERT_DEVICE,
  EDIT_ALERT_DEVICE,
  ASSIGN_ALERT_DEVICE_TO_ALERT_GROUP,
  PROCESS_ALERT_DEVICE_ALERT_GROUP_ASSIGNMENT,
  addAlertGroupAssignment,
  PROCESS_ASSIGNED_ALERT_DEVICE_DETAILS,
  LOAD_ASSIGNED_ALERT_DEVICE_DETAILS,
  LOAD_PROCESS_DETAILS,
  PROCESS_PROCESS_DETAILS,
  addProcessDetails,
  LOAD_ALERT_GROUPS,
  DELETE_ALERT_GROUP,
  PROCESS_DELETE_ALERT_GROUP,
  REMOVE_ALERT_DEVICE_DETAIL,
  LOAD_PARENT_ALERT_GROUPS,
  PROCESS_PARENT_ALERT_GROUPS,
  addParentAlertGroups,
  PROCESS_ALERT_DEVICE_ALERT_GROUP_REMOVAL,
  removeAlertGroupAssignment,
} from '../actions/alertDeviceActions';
import { FormattedMessage } from 'react-intl';
import { messageTypes } from '../../components/ui/AlertMessage';
import {
  DEVICE_ERROR,
  createDeviceAssociation,
} from '../actions/deviceActions';
import { redirect } from '../../utilities/general';
import { updateApiSpinner } from '../actions/uiActions';

/**
 * Load assigned alert devices details
 *
 * Alertdevice endpoint with monitored=true and assigned=true.
 * Includes api spinner flag
 */
export const loadAssignedAlertDevicesDetails = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === LOAD_ASSIGNED_ALERT_DEVICE_DETAILS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertdevice +
        '?monitored=true&assignable=true&assigned=true',
      onSuccess: PROCESS_ASSIGNED_ALERT_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
    store.dispatch(updateApiSpinner({ associatedAlertDevicesReturned: false }));
  }
};

/**
 * Process returned alert devices details
 *
 * If there are paged results, trigger further api calls for the pages.
 * If there are no more results, hide loading and update the
 */

export const processAssignedAlertDevicesDetails = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_ASSIGNED_ALERT_DEVICE_DETAILS) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_ASSIGNED_ALERT_DEVICE_DETAILS,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(
        updateApiSpinner({ associatedAlertDevicesReturned: true })
      );
      store.dispatch(hideLoading());
    }

    const initialArray = action.payload.results;
    const alertDetails = {};
    initialArray.map((device) => {
      alertDetails[device.alert_device_id] = {
        alert_device_id: device.alert_device_id,
        alert_device_type: device.alert_device_type,
        alert_group_id: device.alert_group_id,
        unit: device.unit,
        user: device.user,
        email_address: device.email_address,
        phone_number: device.phone_number,
        interface_name: device.interface_name,
        alert_description: device.alert_description,
        notes: device.notes,
        alert_device_category: device.alert_device_category,
        restrict_to_interface: device.restrict_to_interface,
        retry_after_msg: device.retry_after_msg,
        retry_after_noans: device.retry_after_noans,
        retry_cnt: device.retry_cnt,
        retry_interval: device.retry_interval,
        subtype: device.subtype,
        notify_on_clear: device.notify_on_clear,
        short_msg: device.short_msg,
      };
      return '';
    });

    store.dispatch(addAlertDeviceDetails(alertDetails));
  }
};

/**
 * Load alert device details
 *
 * Gets the device details for a single alert device based on an ID passed to the function
 */
export const loadAlertDeviceDetail = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ALERT_DEVICE_DETAILS) {
    const body = store.getState().auth.token;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertdevice +
        '/' +
        action.payload,
      onSuccess: PROCESS_ALERT_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Load alert device types
 */
export const loadAlertDeviceTypes = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ALERT_DEVICE_TYPES) {
    const body = store.getState().auth.token;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertdevicetype,
      onSuccess: PROCESS_ALERT_DEVICE_TYPES,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Process the loaded alert device details
 *
 * Takes the incoming details and creates an object based on the details to insert into the alert device details object.
 */
export const processAlertDeviceDetail = ({ dispatch }) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_ALERT_DEVICE_DETAILS) {
    dispatch(hideLoading());
    const alertDetail = {
      [action.payload.alert_device_id]: { ...action.payload },
    };

    dispatch(addAlertDeviceDetails(alertDetail));
    if (action.meta.navigate && action.meta.previous) {
      action.meta.navigate(action.meta.previous);
    }
  }
};

/**
 * Get all alert devices details
 */
export const getAllAlertDevices = (store) => (next) => (action) => {
  next(action);
  if (action.type === GET_ALL_ALERT_DEVICES) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertdevice +
        '?monitored=true&assignable=true',
      onSuccess: PROCESS_UNASSIGNED_ALERT_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
    store.dispatch(updateApiSpinner({ alertDevicesReturned: false }));
  }
};

/**
 * Get all alert groups
 */
export const loadAlertGroups = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ALERT_GROUPS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertdevice +
        '?alert_group=true',
      onSuccess: PROCESS_UNASSIGNED_ALERT_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
    store.dispatch(updateApiSpinner({ alertDevicesReturned: false }));
  }
};

export const deleteAlertGroup = (store) => (next) => (action) => {
  next(action);
  if (action.type === DELETE_ALERT_GROUP) {
    const body = null;
    const meta = {
      method: 'DELETE',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertdevice +
        action.alertDeviceId +
        '/',
      onSuccess: PROCESS_DELETE_ALERT_GROUP,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
      alertDeviceId: action.alertDeviceId,
      seq: action.payload,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

export const processDeleteAlertGroup = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_DELETE_ALERT_GROUP) {
    store.dispatch(hideLoading());

    const messageText = (
      <React.Fragment>
        Alert group deleted successfully.
      </React.Fragment>
      
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
    store.dispatch({ type: REMOVE_ALERT_DEVICE_DETAIL, payload: action.meta.alertDeviceId });
    store.dispatch({ type: LOAD_PARENT_ALERT_GROUPS });
  }
};


export const loadParentAlertGroups = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_PARENT_ALERT_GROUPS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].parentalertgroup,
      onSuccess: PROCESS_PARENT_ALERT_GROUPS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

export const processParentAlertGroups = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_PARENT_ALERT_GROUPS) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_PARENT_ALERT_GROUPS,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
    }

    const initialArray = action.payload.results;
    const parentGroups = {};
    initialArray.map((group) => {
      parentGroups[group.seq] = { ...group };
      return '';
    });

    store.dispatch(addParentAlertGroups(parentGroups));
  }
};


/**
 * Load unassigned alert device details
 */
export const loadUnassignedAlertDevicesDetails = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === LOAD_UNASSIGNED_ALERT_DEVICE_DETAILS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertdevice +
        '?monitored=true&assignable=true&assigned=false',
      onSuccess: PROCESS_UNASSIGNED_ALERT_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
    store.dispatch(updateApiSpinner({ alertDevicesReturned: false }));
  }
};

/**
 * Process unassigned alert devices details
 *
 * If there are paged results, trigger api call until there are no more pages then trigger to hide loading and update the loading spinner.
 * Map through results array and reformat alert device details into object to only contain the information needed. This is to reduce the
 * amount of data that needs to be stored.
 */

export const processUnassignedAlertDevicesDetails = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_UNASSIGNED_ALERT_DEVICE_DETAILS) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_UNASSIGNED_ALERT_DEVICE_DETAILS,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
      store.dispatch(updateApiSpinner({ alertDevicesReturned: true }));
    }

    const initialArray = action.payload.results;
    const alertDetails = {};
    initialArray.map((device) => {
      alertDetails[device.alert_device_id] = {
        alert_device_id: device.alert_device_id,
        alert_device_type: device.alert_device_type,
        alert_group_id: device.alert_group_id,
        unit: device.unit,
        user: device.user,
        email_address: device.email_address,
        phone_number: device.phone_number,
        interface_name: device.interface_name,
        alert_description: device.alert_description,
        notes: device.notes,
        alert_device_category: device.alert_device_category,
        restrict_to_interface: device.restrict_to_interface,
        retry_after_msg: device.retry_after_msg,
        retry_after_noans: device.retry_after_noans,
        retry_cnt: device.retry_cnt,
        retry_interval: device.retry_interval,
        subtype: device.subtype,
        notify_on_clear: device.notify_on_clear,
        short_msg: device.short_msg,
        modified: device.modified,
      };
      return '';
    });

    store.dispatch(addAlertDeviceDetails(alertDetails));
  }
};

/**
 * Process alert device types
 *
 * Process the returned device types, map through the results array and add to an object that will then be inserted into the Redux store
 */

export const processAlertDeviceTypes = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ALERT_DEVICE_TYPES) {
    store.dispatch(hideLoading());

    const initialArray = action.payload.results;
    const types = {};
    initialArray.map((device) => {
      types[device.alert_device_type] = { ...device };
      return '';
    });

    store.dispatch(addAlertDeviceTypes(types));
  }
};

/**
 * Load process details
 *
 * This is used when editing a phone number to load the phone interfaces to be used by that phone number.
 */
export const loadProcessDetails = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_PROCESS_DETAILS) {
    const body = store.getState().auth.token;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].process +
        '?process_type=5',
      onSuccess: PROCESS_PROCESS_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Process process details
 *
 * This is used when editing a phone number to load the phone interfaces to be used by that phone number.
 *
 */

export const processProcessDetails = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_PROCESS_DETAILS) {
    store.dispatch(hideLoading());

    const initialArray = action.payload.results;
    const types = {};
    initialArray.map((device) => {
      types[device.id] = { ...device };
      return '';
    });

    store.dispatch(addProcessDetails(types));
  }
};

/**
 * Load assigned alert group list
 */
export const loadAssignedAlertGroupList = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ASSIGNED_ALERT_GROUP_LIST) {
    const body = null;
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertgroup,
      onSuccess: PROCESS_ASSIGNED_ALERT_GROUP_LIST,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Process assigned alert group list
 *
 * If there are paged results, trigger api call for next page. If not, hide loading.
 * Create an object of the existing alert groups by spreading the data from the store.
 * Map through the results and place them into the existing object then dispatch the
 * object to be inserted into the Redux store.
 */

export const processAssignedAlertGroupList = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ASSIGNED_ALERT_GROUP_LIST) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_ASSIGNED_ALERT_GROUP_LIST,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
    }
    const initialArray = action.payload.results;
    const alertGroup = {};
    initialArray.map((device) => {
      alertGroup[device.seq] = { ...device };
      return '';
    });

    
    const base = action.payload.previous
      ? store.getState().devices.assignedAlertGroups
      : {};
    store.dispatch(addAlertGroupList({ ...base, ...alertGroup }));
  }
};

/**
 * Load Alert Device Categories
 */

export const loadAlertDeviceCategories = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ALERT_DEVICE_CATEGORIES) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertdevicecategory,
      onSuccess: PROCESS_ALERT_DEVICE_CATEGORIES,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Process alert device categories
 *
 * If there are paged results, trigger api call for next page. If not, hide loading.
 * Map through the results and place them into an object then dispatch the
 * object to be inserted into the Redux store.
 */

export const processAlertDeviceCategories = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ALERT_DEVICE_CATEGORIES) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_ALERT_DEVICE_CATEGORIES,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
    }

    const initialArray = action.payload.results;
    const alertCategories = {};
    initialArray.map((cat) => {
      alertCategories[cat.id] = { ...cat };
      return '';
    });

    store.dispatch(addAlertDeviceCategories(alertCategories));
  }
};

/**
 * Create then associate an alert device
 *
 * This is used in the process when a user has a directory phone number or email
 * and a device is going to be created from that information.
 */

export const createAssociateAlertDevice = (store) => (next) => (action) => {
  next(action);
  if (action.type === CREATE_ASSOCIATE_ALERT_DEVICE) {
    const body = { ...action.payload };

    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertdevice,
      onSuccess: PROCESS_ASSOCIATE_NEW_ALERT_DEVICE,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      previous: action.previous,
      formSteps: action.formSteps,
      user: action.payload.user,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Edit an alert device
 *
 * Check to see if the option to associate the device to an alert group is checked in the data
 * and add that item to the form queue if it is.
 */

export const editAlertDevice = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_ALERT_DEVICE) {
    const body = action.payload;

    if (!body.restrict_to_interface) {
      body.interface_name = null;
    }

    const nextSteps = [];
    if (body.associate_alert_group) {
      nextSteps.push('associateAlertGroup');
    }

    const prevSteps = ['alertDeviceCreate'];

    const formSteps = {
      type: 'users',
      nextSteps,
      prevSteps,
      id: body.alert_device_id,
    };

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].alertdevice +
        body.alert_device_id +
        '/',
      onSuccess: PROCESS_ALERT_DEVICE_DETAILS,
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      previous: action.previous,
      formSteps: formSteps,
      user: action.payload.user,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Process and associate a new alert device
 *
 * This creates an object containing the alert device details then adds that object to Redux and
 * redirects the interface to the correct next page. It submits a message that the device has been
 * created then dispatches the data to create a device association for the device to associate
 * it to the user or unit that it was created for.
 */

export const processAssociateNewAlertDevice = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ASSOCIATE_NEW_ALERT_DEVICE) {
    store.dispatch(hideLoading());

    const alertDetail = {
      [action.payload.alert_device_id]: { ...action.payload },
    };

    let url = '/devices/' + action.payload.alert_device_id + '/view/';
    redirect(action, url);

    const alertType = store.getState().devices.alertDeviceTypes[
      action.payload.alert_device_type
    ].description;

    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="alertDeviceMiddleware.alertDeviceCreationText"
          description="Notification that an alert device has been associated"
          defaultMessage="{type}: {device} has been Created"
          values={{
            type: alertType,
            device: action.payload.alert_description,
          }}
        />
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
    const payload = {
      alert_device: action.payload.alert_device_id,
      assigned_user: action.meta.user,
    };
    store.dispatch(addAlertDeviceDetails(alertDetail));
    store.dispatch(
      createDeviceAssociation(
        payload,
        action.meta.navigate,
        action.meta.previous,
        action.meta.formSteps
      )
    );
  }
};

/**
 * Assign an alert device to a group
 *
 * This adds a device to an alert group. The endpoint is only set up to process
 * a single item at a time so the array of updated/added items are mapped over to
 * send individual api calls for each at a time. It sends the showLoading trigger
 * and the alert device id, alert device type, and the group to associate them to.
 *
 * The form is set currently to only allow adding to the groups, not removing. So
 * the commented out part is code that would be used to remove devices from groups.
 *
 *  *****PLEASE NOTE: This code has not been tested so you should probably
 *       update it and verify that it's functioning before using it. *****
 */
export const assignAlertDeviceToGroup = (store) => (next) => (action) => {
  next(action);
  if (action.type === ASSIGN_ALERT_DEVICE_TO_ALERT_GROUP) {
    const groups = action.payload.alert_group_id;
    const deviceType = action.payload.alert_device_type;
    const deviceId = action.payload.alert_device_id;
    const initialGroups = action.payload.initialGroups;
    const filteredGroups = groups.filter((g) => !initialGroups.includes(g));
    
    // Find groups to remove (were in initialGroups but not in new groups)
    const removedGroups = initialGroups.filter(g => !groups.includes(g));
    const groupsObj = store.getState().devices.assignedAlertGroups;

    // Find seq IDs for relationships to remove
    const removedSeqIds = Object.values(groupsObj)
      .filter(g => 
        g.alert_device_id === deviceId && 
        removedGroups.includes(g.alert_group_id)
      )
      .map(g => g.seq);

    // ADD new group assignments
    filteredGroups.map((g) => {
      const body = {
        alert_device_type: deviceType,
        alert_device_id: deviceId,
        alert_group_id: g,
      };
      const meta = {
        method: 'POST',
        url:
          apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertgroup + '/',
        onSuccess: PROCESS_ALERT_DEVICE_ALERT_GROUP_ASSIGNMENT,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: true,
        navigate: action.navigate,
        previous: action.previous,
        formSteps: action.formSteps,
        user: action.payload.user,
      };
      store.dispatch(showLoading());
      store.dispatch(apiRequest(body, meta));
      return '';
    });

    // REMOVE group assignments by seq ID
    removedSeqIds.forEach(seqId => {
      const meta = {
        method: "DELETE",
        url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertgroup + seqId + '/',
        onSuccess: PROCESS_ALERT_DEVICE_ALERT_GROUP_REMOVAL,
        onError: DEVICE_ERROR,
        auth: store.getState().auth.token,
        body: false, 
        navigate: action.navigate, 
        previous: action.previous,
        formSteps: action.formSteps,
        user: action.payload.user,
        seqId: seqId
      };
      store.dispatch(showLoading());
      store.dispatch(apiRequest(null, meta)); 
    });
    
  }
};

/**
 * Process assigning an alert device to a group
 *
 * When the api call is successful, this adds the returned data to the redux store by
 * first dispatching the hide loading trigger and then taking the payload, creating an
 * object from the contents, then adding the object to the redux store.
 *
 * The confirmation message is generated by looking up the alert device id description
 * and alert group id description from the redux store as well as the alert device
 * type description from the redux store.
 */

export const processAlertDeviceAlertGroupAssignment = (store) => (next) => (
  action
) => {
  next(action);
  if (action.type === PROCESS_ALERT_DEVICE_ALERT_GROUP_ASSIGNMENT) {
    store.dispatch(hideLoading());
    const alertDetail = {
      [action.payload.seq]: { ...action.payload },
    };
    let url = '/devices/' + action.payload.alert_device_id + '/view/';
    redirect(action, url);

    const alertType = store.getState().devices.alertDeviceTypes[
      action.payload.alert_device_type
    ].description;

    const alertDevice = store.getState().devices.alertDeviceDetails[
      action.payload.alert_device_id
    ].alert_description;
    const alertGroup = store.getState().devices.alertDeviceDetails[
      action.payload.alert_group_id
    ].alert_description;

    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="alertDeviceMiddleware.alertGroupAssociatedText"
          description="Notification that an alert device has been associated"
          defaultMessage="{type}: {device} has been associated with group: {group}"
          values={{
            type: alertType,
            device: alertDevice,
            group: alertGroup,
          }}
        />
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
    store.dispatch(addAlertGroupAssignment(alertDetail));
  }
};

export const createAssociateMassNotificationDevice = (store) => (next) => (action) => {
  next(action);
  if (action.type === 'CREATE_ASSOCIATE_MASS_NOTIFICATION_DEVICE') {
    const deviceInfo = action.payload;
    
    console.log('createAssociateMassNotificationDevice - deviceInfo received:', deviceInfo);
    
    // Generate a default description if none provided
    const defaultDescription = deviceInfo.alert_description || `Mass notification device`;
    
    // Prepare the mass notification data
    const massNotificationData = {
      user: deviceInfo.user,
      alert_device_type: 54, // Mass notification type
      alert_description: defaultDescription,
      phone_number: deviceInfo.primary_phone || '',
      work_phone: deviceInfo.work_phone || '', 
      cell_phone_number: deviceInfo.text_phone || '', 
      other_phone: deviceInfo.secondary_phone || '',
      work_email: deviceInfo.work_email || '',
      other_email: deviceInfo.personal_email || '',
      email_address: deviceInfo.secondary_email || '',
      notes: deviceInfo.notes || 'created during user edit',
    };
    
    
    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].alertdevice,
      onSuccess: 'PROCESS_ASSOCIATE_MASS_NOTIFICATION_DEVICE',
      onError: DEVICE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: action.formSteps,
      previous: action.previous,
      user: action.payload.user,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(massNotificationData, meta));
  }
};

/**
 * Process and associate a new mass notification device
 *
 * This creates the device association after the mass notification device is created
 */
export const processAssociateMassNotificationDevice = (store) => (next) => (action) => {
  next(action);
  if (action.type === 'PROCESS_ASSOCIATE_MASS_NOTIFICATION_DEVICE') {
    console.log('Mass notification device created successfully:', action.payload);
    
    // Add the device to Redux store
    const alertDetail = {
      [action.payload.alert_device_id]: { ...action.payload },
    };
    store.dispatch(addAlertDeviceDetails(alertDetail));
    
    const userId = action.meta.user;
    const deviceId = action.payload.alert_device_id;
    
    // Create the device association using the existing pattern
    const payload = {
      alert_device: deviceId,
      assigned_user: userId,
    };
    
    store.dispatch(
      createDeviceAssociation(
        payload,
        action.meta.navigate,
        action.meta.previous,
        action.meta.formSteps
      )
    );

    // Show success message
    const alertType = store.getState().devices.alertDeviceTypes[
      action.payload.alert_device_type
    ]?.description || 'Mass Notification Device';

    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="alertDeviceMiddleware.massNotificationDeviceCreationText"
          description="Notification that a mass notification device has been created"
          defaultMessage="{type}: {device} has been Created and Assigned"
          values={{
            type: alertType,
            device: action.payload.alert_description,
          }}
        />
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
    store.dispatch(hideLoading());
  }
};

export const processAlertDeviceAlertGroupRemoval = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ALERT_DEVICE_ALERT_GROUP_REMOVAL) {
    store.dispatch(hideLoading());
    
    // Use the seq ID from meta since DELETE response is empty
    const seqId = action.meta.seqId;
    store.dispatch(removeAlertGroupAssignment(seqId));

    let url = '/devices/' + action.meta.formSteps.id + '/view/';
    redirect(action, url);
    
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="alertDeviceMiddleware.alertGroupRemovedText"
          description="Notification that an alert device has been removed from group"
          defaultMessage="Alert device removed from group successfully"
        />
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
  }
};

export const alertDevicesMdl = [
  loadAssignedAlertDevicesDetails,
  processAssignedAlertDevicesDetails,
  loadAlertDeviceDetail,
  processAlertDeviceDetail,
  loadAlertDeviceTypes,
  processAlertDeviceTypes,
  loadUnassignedAlertDevicesDetails,
  processUnassignedAlertDevicesDetails,
  getAllAlertDevices,
  loadAssignedAlertGroupList,
  processAssignedAlertGroupList,
  loadAlertDeviceCategories,
  processAlertDeviceCategories,
  createAssociateAlertDevice,
  processAssociateNewAlertDevice,
  editAlertDevice,
  assignAlertDeviceToGroup,
  processAlertDeviceAlertGroupAssignment,
  loadProcessDetails,
  processProcessDetails,
  loadAlertGroups,
  deleteAlertGroup,
  processDeleteAlertGroup,
  loadParentAlertGroups,
  processParentAlertGroups,
  createAssociateMassNotificationDevice,
  processAssociateMassNotificationDevice,
  processAlertDeviceAlertGroupRemoval,
];
